import { addHours, addMinutes, format } from "date-fns";
import { Ride } from "../../queries/models/ride.model";
import { Button } from "primereact/button";
import { useMemo } from "react";
import { isWithinInterval } from "date-fns/fp";
import { PlannedRide } from "./PlannedRide";
import { PlannedTreatment } from "./PlannedTreatment";
import { authService } from "../../services/auth.service";
import { useNavigate } from "react-router-dom";

export interface PlanRowProps {
  rides: Ride[];
  treatments?: any[];
  date: Date;
  onAddNewRide: (date: Date) => void;
  onEditRide: (ride: Ride) => void;
  shortNames?: boolean;
  showInstructor?: boolean;
}

export function PlanRow({
  rides,
  treatments = [],
  date,
  shortNames = true,
  showInstructor = true,
  onAddNewRide,
  onEditRide,
}: PlanRowProps) {
  const navigate = useNavigate();
  const isAdmin = useMemo(() => {
    return authService.isAdmin();
  }, []);

  const isAsistant = useMemo(() => {
    return authService.isAssistant();
  }, []);

  const dateRides = useMemo(() => {
    const result = rides
      .filter((ride) => {
        const validRange = {
          start:
            ride.duration > 1 ? addHours(date, -(ride.duration - 1)) : date,
          end: addMinutes(date, 59),
        };
        return isWithinInterval(validRange, ride.date);
      })
      .sort((a, b) => {
        const nameA = a.user?.name?.toLowerCase() || "";
        const nameB = b.user?.name?.toLowerCase() || "";
        return nameA.localeCompare(nameB);
      });

    return result;
  }, [rides, date, shortNames, showInstructor]);

  const dateTreatments = useMemo(() => {
    const result = (treatments ?? [])
      .filter((t: any) => {
        const tDate = t?.date instanceof Date ? t.date : new Date(t?.date);
        return isWithinInterval({ start: date, end: addMinutes(date, 59) }, tDate);
      })
      .sort((a: any, b: any) => (a.treatmentType?.name ?? '').toString().localeCompare((b.treatmentType?.name ?? '').toString(), 'pl'));
    return result;
  }, [treatments, date]);

  return (
    <div className="w-full flex border-bottom py-3 p-2">
      <div className="pr-2 align-self-center">
        <span className="text-xl">{format(date, "HH:mm")}</span>
      </div>
      <div className="w-full flex align-self-center flex-wrap">
        {dateRides.map((ride) => (
          <PlannedRide
            key={`${ride.id}${ride.scheduledRideId}${ride.riderId}`}
            ride={ride}
            shortNames={isAsistant ? true : shortNames}
            showInstructor={showInstructor}
            onSelected={() => onEditRide(ride)}
          />
        ))}
        {dateTreatments.map((t: any) => (
          <PlannedTreatment key={`t${t.id}`} treatment={t} onSelected={(tt) => navigate(`/app/treatments/${tt.id}`)} />
        ))}
      </div>
      <div className="align-self-center text-right">
        {isAdmin && (
          <Button
            icon="pi pi-plus pull-right menu-icon-color"
            onClick={() => onAddNewRide(date)}
          />
        )}
      </div>
    </div>
  );
}
